import axios, { AxiosRequestConfig } from 'axios';
import { getAccessToken, authServiceSingleton } from './authService';
import { environment } from '../config/environment';

const BASE_URL = environment.VITE_APP_API_BASE_URL;
export const SERVER_BUSY_MESSAGE = 'The server is busy, please try again later.';

export const api = axios.create({
  baseURL: BASE_URL,
  timeout: 45000,
  headers: {
    'Content-Type': 'application/json',
  },
});

let lastTokenExpiry: number | null = null;
let useReducedScopes = false;

/**
 * Acquire Graph API token
 */
export const getGraphToken = async (): Promise<string | null> => {
  const instance = await authServiceSingleton.getMsalInstance();
  const accounts = instance.getAllAccounts();

  if (accounts.length === 0) {
    return null;
  }

  const allScopes = [
    'Calendars.ReadWrite',
    'Calendars.ReadWrite.Shared',
    'Channel.ReadBasic.All',
    'ChannelMessage.Read.All',
    'Chat.Create',
    'Chat.Read',
    'Chat.ReadWrite.All',
    'ChatMember.ReadWrite',
    'ChatMessage.Read',
    'ChatMessage.Send',
    'Contacts.Read',
    'Directory.AccessAsUser.All',
    'Directory.Read.All',
    'email',
    'Files.ReadWrite.All',
    'Mail.ReadWrite',
    'Mail.ReadWrite.Shared',
    'Mail.Send',
    'Mail.Send.Shared',
    'MailboxSettings.Read',
    'offline_access',
    'OnlineMeetingRecording.Read.All',
    'OnlineMeetings.Read',
    'OnlineMeetings.ReadWrite',
    'OnlineMeetingTranscript.Read.All',
    'openid',
    'Presence.Read.All',
    'profile',
    'People.Read',
    'Sites.ReadWrite.All',
    'Tasks.Read',
    'Tasks.Read.Shared',
    'Tasks.ReadWrite',
    'Tasks.ReadWrite.Shared',
    'Team.ReadBasic.All',
    'User.Read',
    'User.Read.All',
    'User.ReadBasic.All',
  ];

  // Fallback scopes for incognito mode (excludes problematic scopes)
  // 'OnlineMeetingRecording.Read.All'
  // 'OnlineMeetings.Read'
  // 'OnlineMeetings.ReadWrite'
  // 'OnlineMeetingTranscript.Read.All'
  // 'Sites.ReadWrite.All'
  const incognitoSafeScopes = [
    'Calendars.ReadWrite',
    'Calendars.ReadWrite.Shared',
    'Channel.ReadBasic.All',
    'ChannelMessage.Read.All',
    'Chat.Create',
    'Chat.Read',
    'Chat.ReadWrite.All',
    'ChatMember.ReadWrite',
    'ChatMessage.Read',
    'ChatMessage.Send',
    'Contacts.Read',
    'Directory.AccessAsUser.All',
    'Directory.Read.All',
    'email',
    'Mail.ReadWrite',
    'Mail.ReadWrite.Shared',
    'Mail.Send',
    'Mail.Send.Shared',
    'MailboxSettings.Read',
    'offline_access',
    'openid',
    'Presence.Read.All',
    'profile',
    'People.Read',
    'Tasks.Read',
    'Tasks.Read.Shared',
    'Tasks.ReadWrite',
    'Tasks.ReadWrite.Shared',
    'Team.ReadBasic.All',
    'User.Read',
    'User.Read.All',
    'User.ReadBasic.All',
  ];

  const tryAcquireToken = async (scopes: string[]) => {
    const response = await instance.acquireTokenSilent({
      scopes,
      account: accounts[0],
    });
    return { token: response.accessToken, expiry: response.expiresOn?.getTime() ?? null };
  };

  if (useReducedScopes) {
    try {
      const { token, expiry } = await tryAcquireToken(incognitoSafeScopes);

      if (expiry !== null && expiry !== lastTokenExpiry) {
        console.warn('Token refreshed, retrying full scopes');
        lastTokenExpiry = expiry;
        useReducedScopes = false;
      } else {
        return token;
      }
    } catch (error) {
      useReducedScopes = false;
      throw error;
    }
  }

  try {
    const { token, expiry } = await tryAcquireToken(allScopes);
    lastTokenExpiry = expiry;
    return token;
  } catch (error) {
    if (!useReducedScopes) {
      console.warn('Full scopes failed, using safe scopes:', error);
    }
    useReducedScopes = true;

    try {
      const { token, expiry } = await tryAcquireToken(incognitoSafeScopes);
      lastTokenExpiry = expiry;
      return token;
    } catch (fallbackError) {
      console.warn('Graph token acquisition failed:', fallbackError);
      return null;
    }
  }
};

api.interceptors.request.use(async (config) => {
  try {
    const [backendToken, graphToken] = await Promise.all([getAccessToken(), getGraphToken()]);

    // Add backend API token to Authorization header
    if (backendToken) {
      config.headers.Authorization = `Bearer ${backendToken}`;
    }

    // Add Graph API token to custom header
    if (graphToken) {
      config.headers['X-MS-Graph-Token'] = graphToken;
    }

    // Send the browser's IANA timezone so the backend can localise dates

    config.headers['X-Timezone'] = Intl.DateTimeFormat().resolvedOptions().timeZone;

    return config;
  } catch (error) {
    console.error('Token acquisition failed:', error);
    throw error;
  }
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401 || error.response?.status === 403) {
      console.warn(`Authentication error (${error.response.status}): Access may have been revoked`);
    }
    throw error;
  },
);

export const isApiTimeoutError = (error: unknown): boolean => {
  if (!axios.isAxiosError(error)) {
    return false;
  }

  const message = typeof error.message === 'string' ? error.message.toLowerCase() : '';
  return (
    error.code === 'ECONNABORTED' ||
    error.response?.status === 408 ||
    error.response?.status === 504 ||
    message.includes('timeout')
  );
};

export const isNetworkOfflineError = (error: unknown): boolean => {
  if (!navigator.onLine) return true;
  if (axios.isAxiosError(error) && !error.response && error.code !== 'ECONNABORTED') return true;
  return false;
};

export const getApiErrorMessage = (
  error: unknown,
  fallbackMessage: string,
  timeoutMessage = SERVER_BUSY_MESSAGE,
): string => {
  if (isNetworkOfflineError(error)) {
    return 'You are offline. Please check your connection.';
  }

  if (isApiTimeoutError(error)) {
    return timeoutMessage;
  }

  if (axios.isAxiosError(error)) {
    const apiMessage = error.response?.data?.message || error.response?.data?.detail;
    if (typeof apiMessage === 'string' && apiMessage.trim().length > 0) {
      return apiMessage;
    }
  }

  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message;
  }

  return fallbackMessage;
};

export const fetcher = <T>(url: string, config?: AxiosRequestConfig<void>) =>
  api
    .get<T>(url, {
      ...config,
    })
    .then(({ data }) => data);

export const post = <T>(url: string, body: unknown, config?: AxiosRequestConfig): Promise<T> =>
  api.post<T>(url, body, { ...config }).then(({ data }) => data);

export const put = <T>(url: string, body: unknown, config?: AxiosRequestConfig): Promise<T> =>
  api.put<T>(url, body, { ...config }).then(({ data }) => data);

export const patch = <T>(url: string, body: unknown, config?: AxiosRequestConfig): Promise<T> =>
  api.patch<T>(url, body, { ...config }).then(({ data }) => data);

export const del = <T>(url: string, config?: AxiosRequestConfig, getStatusCode?: boolean): Promise<T> => {
  if (getStatusCode) {
    return api.delete(url, { ...config }).then(({ status }) => status as unknown as T);
  }
  return api.delete<T>(url, { ...config }).then(({ data }) => data);
};

export const postWithFiles = <T>(url: string, data: object, files: File[], config?: AxiosRequestConfig): Promise<T> => {
  const formData = new FormData();

  // Add non-file data as individual form fields
  Object.entries(data).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      // Handle arrays by appending each item separately
      if (Array.isArray(value)) {
        value.forEach((item) => {
          formData.append(key, String(item));
        });
      } else if (typeof value === 'object') {
        formData.append(key, JSON.stringify(value));
      } else {
        formData.append(key, String(value));
      }
    }
  });

  // Add files with array notation
  files.forEach((file) => {
    formData.append('files', file);
  });

  return api
    .post<T>(url, formData, {
      ...config,
      headers: {
        'Content-Type': 'multipart/form-data',
        ...config?.headers,
      },
    })
    .then(({ data }) => data);
};

export default {
  fetcher,
  post,
  put,
  patch,
  del,
  postWithFiles,
};
